// Mock data for the eco-friendly application

export const services = [
  {
    id: 1,
    title: "Sustainable Chemistry Solutions",
    description: "Advanced eco-friendly chemical formulations for industrial applications",
    icon: "🌱",
    image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&h=300&fit=crop",
    features: ["Non-toxic formulations", "Biodegradable compounds", "Renewable feedstocks"]
  },
  {
    id: 2,
    title: "Green Manufacturing Processes",
    description: "Innovative manufacturing methods that minimize environmental impact",
    icon: "♻️",
    image: "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=400&h=300&fit=crop",
    features: ["Zero waste production", "Energy efficiency", "Water conservation"]
  },
  {
    id: 3,
    title: "Circular Economy Solutions",
    description: "Complete lifecycle management for sustainable chemical products",
    icon: "🌍",
    image: "https://images.unsplash.com/photo-1558618666-e29c2c84c87c?w=400&h=300&fit=crop",
    features: ["Waste upcycling", "Resource recovery", "Closed-loop systems"]
  },
  {
    id: 4,
    title: "Environmental Consulting",
    description: "Expert guidance for transitioning to sustainable chemical practices",
    icon: "🌿",
    image: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=400&h=300&fit=crop",
    features: ["Sustainability audits", "Compliance support", "Strategy development"]
  },
  {
    id: 5,
    title: "Bio-Based Materials",
    description: "Innovative materials derived from renewable biological resources",
    icon: "🌾",
    image: "https://images.unsplash.com/photo-1574263867128-a3d5c1b1deac?w=400&h=300&fit=crop",
    features: ["Plant-based polymers", "Agricultural waste utilization", "Biodegradable alternatives"]
  },
  {
    id: 6,
    title: "Clean Technology Research",
    description: "Cutting-edge research and development for sustainable technologies",
    icon: "🔬",
    image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&h=300&fit=crop",
    features: ["Green catalysis", "Novel synthesis methods", "Process optimization"]
  }
];

export const applications = [
  {
    id: 1,
    category: "Textile Innovation",
    title: "Eco-Textile Solutions",
    description: "Sustainable chemical solutions for modern textile industry",
    image: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=400&h=300&fit=crop",
    industries: ["Fashion", "Activewear", "Home Textiles"]
  },
  {
    id: 2,
    category: "Industrial Applications",
    title: "Green Industrial Chemicals",
    description: "Environmentally responsible chemicals for industrial processes",
    image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=400&h=300&fit=crop",
    industries: ["Manufacturing", "Automotive", "Electronics"]
  },
  {
    id: 3,
    category: "Agricultural Solutions",
    title: "Sustainable Agriculture",
    description: "Bio-based solutions for sustainable farming practices",
    image: "https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=400&h=300&fit=crop",
    industries: ["Organic Farming", "Crop Protection", "Soil Enhancement"]
  },
  {
    id: 4,
    category: "Personal Care",
    title: "Natural Personal Care",
    description: "Safe and sustainable ingredients for personal care products",
    image: "https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=300&fit=crop",
    industries: ["Cosmetics", "Skincare", "Natural Products"]
  }
];

export const projects = [
  {
    id: 1,
    title: "ReactEVO®",
    description: "Converting ocean waste into valuable chemical feedstocks",
    image: "/images/polyester-fabric.webp",
    category: "Circular Economy",
    date: "2024",
    status: "Completed",
    impact: "50,000 kg of plastic waste processed"
  },
  {
    id: 2,
    title: "REVECOL®",
    description: "First carbon-neutral chemical production facility",
    image: "/images/elastane-blend-fabric.jpg",
    category: "Clean Technology",
    date: "2024",
    status: "In Progress",
    impact: "85% reduction in carbon footprint"
  },
  {
    id: 3,
    title: "ReactEVO® TWE Conc",
    description: "Revolutionary bio-based textile treatment solutions",
    image: "/images/polycotton-blend-fabric.jpg",
    category: "Sustainable Materials",
    date: "2023",
    status: "Completed",
    impact: "100% biodegradable textile additives"
  },
  {
    id: 4,
    title: "Laucol ASP",
    description: "Converting agricultural residues into high-value chemicals",
    image: "/images/cellulosic-dark-fabric-hd.jpg",
    category: "Resource Recovery",
    date: "2023",
    status: "Completed",
    impact: "1000 tons of agricultural waste diverted"
  },
  {
    id: 5,
    title: "REVECOL® XT",
    description: "Next-generation environmentally safe industrial solvents",
    image: "/images/cellulosic-light-fabric.jpg",
    category: "Chemical Innovation",
    date: "2024",
    status: "In Progress",
    impact: "90% reduction in VOC emissions"
  }
];

export const blogPosts = [
  {
    id: 1,
    title: "The Future of Sustainable Chemistry: Green Innovation Trends",
    excerpt: "Exploring the latest developments in eco-friendly chemical processes and their impact on industrial sustainability.",
    content: `
      <h2>Introduction to Green Chemistry</h2>
      <p>The chemical industry is undergoing a revolutionary transformation towards sustainability. Green chemistry, also known as sustainable chemistry, is designing chemical products and processes that reduce or eliminate the generation of hazardous substances.</p>
      
      <h2>Key Principles of Green Chemistry</h2>
      <p>The twelve principles of green chemistry provide a framework for designing environmentally benign chemical processes:</p>
      <ul>
        <li>Prevention of waste</li>
        <li>Atom economy</li>
        <li>Less hazardous chemical syntheses</li>
        <li>Safer chemicals by design</li>
        <li>Safer solvents and auxiliaries</li>
      </ul>
      
      <h2>Current Trends and Innovations</h2>
      <p>Recent advances include the development of bio-based feedstocks, catalytic processes that operate under mild conditions, and the use of renewable energy sources in chemical manufacturing.</p>
      
      <h2>Looking Forward</h2>
      <p>The future of sustainable chemistry lies in circular economy approaches, where waste from one process becomes the input for another, creating closed-loop systems that minimize environmental impact.</p>
    `,
    image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&h=500&fit=crop",
    author: "Dr. Sarah Green",
    date: "2024-01-15",
    category: "Innovation",
    readTime: "5 min read",
    tags: ["sustainability", "green chemistry", "innovation"]
  },
  {
    id: 2,
    title: "Circular Economy in Chemical Manufacturing",
    excerpt: "How circular economy principles are revolutionizing the chemical industry and creating sustainable value chains.",
    content: `
      <h2>Understanding Circular Economy</h2>
      <p>The circular economy is an economic model that aims to eliminate waste and maximize resource efficiency by keeping products, materials, and resources in use for as long as possible.</p>
      
      <h2>Application in Chemical Industry</h2>
      <p>Chemical manufacturers are implementing circular economy principles through:</p>
      <ul>
        <li>Waste-to-resource conversion</li>
        <li>Product design for recyclability</li>
        <li>Industrial symbiosis</li>
        <li>Closed-loop manufacturing</li>
      </ul>
      
      <h2>Success Stories</h2>
      <p>Leading companies are already demonstrating the viability of circular approaches, with some achieving zero waste to landfill and significant reductions in raw material consumption.</p>
    `,
    image: "https://images.unsplash.com/photo-1558618666-e29c2c84c87c?w=800&h=500&fit=crop",
    author: "Prof. Michael Chen",
    date: "2024-01-10",
    category: "Circular Economy",
    readTime: "7 min read",
    tags: ["circular economy", "waste reduction", "sustainability"]
  },
  {
    id: 3,
    title: "Bio-Based Materials: The Next Frontier",
    excerpt: "Exploring the potential of renewable biological resources in creating sustainable chemical products.",
    content: `
      <h2>What are Bio-Based Materials?</h2>
      <p>Bio-based materials are derived from renewable biological resources rather than finite fossil resources. They represent a significant opportunity for the chemical industry to reduce its environmental footprint.</p>
      
      <h2>Types of Bio-Based Feedstocks</h2>
      <p>Common sources include:</p>
      <ul>
        <li>Agricultural residues</li>
        <li>Forestry waste</li>
        <li>Algae biomass</li>
        <li>Food waste</li>
        <li>Municipal solid waste</li>
      </ul>
      
      <h2>Applications and Benefits</h2>
      <p>Bio-based materials are being used to create biodegradable plastics, natural dyes, sustainable surfactants, and many other products that traditionally relied on petrochemical feedstocks.</p>
    `,
    image: "https://images.unsplash.com/photo-1574263867128-a3d5c1b1deac?w=800&h=500&fit=crop",
    author: "Dr. Emily Rodriguez",
    date: "2024-01-05",
    category: "Materials Science",
    readTime: "6 min read",
    tags: ["biomaterials", "renewable resources", "innovation"]
  },
  {
    id: 4,
    title: "Water Conservation in Chemical Processing",
    excerpt: "Innovative approaches to reducing water consumption and improving water quality in chemical manufacturing.",
    content: `
      <h2>The Water Challenge</h2>
      <p>The chemical industry is one of the largest consumers of water globally. Reducing water usage and improving water treatment processes are critical for sustainable operations.</p>
      
      <h2>Water-Efficient Technologies</h2>
      <p>New technologies are helping companies reduce their water footprint:</p>
      <ul>
        <li>Closed-loop water systems</li>
        <li>Advanced membrane technologies</li>
        <li>Solvent-free processes</li>
        <li>Water recycling and reuse</li>
      </ul>
      
      <h2>Case Studies</h2>
      <p>Several chemical companies have achieved remarkable water savings, with some facilities reducing water consumption by over 80% through innovative process design and water management strategies.</p>
    `,
    image: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=800&h=500&fit=crop",
    author: "Dr. James Park",
    date: "2023-12-28",
    category: "Environmental",
    readTime: "4 min read",
    tags: ["water conservation", "process optimization", "sustainability"]
  }
];

export const teamMembers = [
  {
    id: 1,
    name: "Kumar Narayanan",
    position: "Managing Director",
    image: "/images/kumar-narayanan.jpg",
    bio: "Visionary leader driving sustainable innovation and strategic growth.",
    expertise: ["Leadership", "Strategy", "Sustainable Development"]
  }
];

export const companyStats = [
  {
    number: "50+",
    label: "Sustainable Products",
    icon: "🌱"
  },
  {
    number: "85%",
    label: "Waste Reduction",
    icon: "♻️"
  },
  {
    number: "100+",
    label: "Global Partners",
    icon: "🤝"
  },
  {
    number: "15",
    label: "Years Experience",
    icon: "🏆"
  }
];

export const testimonials = [
  {
    id: 1,
    text: "Working with this company has transformed our approach to sustainable manufacturing. Their green chemistry solutions have helped us reduce our environmental footprint by 60%.",
    author: "Maria Santos",
    position: "Sustainability Director",
    company: "EcoTech Industries",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop"
  },
  {
    id: 2,
    text: "The bio-based materials they developed for our textile applications exceeded all our expectations. Quality, performance, and sustainability all in one solution.",
    author: "David Johnson",
    position: "R&D Manager",
    company: "Green Fabrics Co",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop"
  },
  {
    id: 3,
    text: "Their circular economy expertise helped us redesign our entire production process, resulting in zero waste to landfill and significant cost savings.",
    author: "Lisa Wang",
    position: "Operations Director",
    company: "Sustainable Solutions Ltd",
    image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop"
  }
];